import { motion } from "framer-motion";
import { Check } from "lucide-react";

const CAPABILITIES = [
  { name: "Automated image segmentation", detail: "Pixel-precise organ and lesion boundaries on every slice" },
  { name: "Anomaly detection and classification", detail: "Flags and categorizes suspicious findings by pathology" },
  { name: "Quantitative measurements", detail: "Volumes, diameters and densities — reproducible to the millimeter" },
  { name: "Treatment planning assistance", detail: "Structured contours and staging data for oncology workflows" },
  { name: "Historical comparison analysis", detail: "Automatic prior-study matching and interval change tracking" },
  { name: "Report generation", detail: "Draft structured reports ready for radiologist sign-off" },
];

export default function ClinicalCapabilities() {
  return (
    <section id="capabilities" className="bg-surface py-24 md:py-32 border-t border-slate-200" data-testid="capabilities-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5 md:sticky md:top-28 self-start">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-brand mb-6"
          >
            In the reading room
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-[0.95] mb-6"
          >
            Clinical
            <br />
            <span className="text-mute">capabilities.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-mute text-base leading-relaxed max-w-md"
          >
            Six production-grade capabilities that plug directly into the diagnostic workflow — from acquisition to signed report.
          </motion.p>
        </div>

        <div className="md:col-span-7">
          {CAPABILITIES.map((c, i) => (
            <motion.div
              key={c.name}
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              className="group flex items-start gap-6 py-7 border-t border-slate-200 last:border-b hover:bg-slate-50 transition-colors duration-300 px-2 -mx-2"
              data-testid={`capability-${i}`}
            >
              <span className="font-mono text-xs text-mute pt-1.5 shrink-0 w-8">{String(i + 1).padStart(2, "0")}</span>
              <span className="w-8 h-8 shrink-0 border border-slate-200 flex items-center justify-center group-hover:border-brand group-hover:bg-brand group-hover:text-white text-brand transition-colors duration-300 mt-0.5">
                <Check className="w-4 h-4" />
              </span>
              <div>
                <h3 className="font-display text-lg sm:text-xl font-semibold tracking-tight">{c.name}</h3>
                <p className="text-mute text-sm leading-relaxed mt-1">{c.detail}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
